import type { AiActionMode, AiProviderKind } from '@xphoto/shared';
import { AI_API_KEY_ENV_PREFIX } from '@xphoto/shared';
import type { MessageKey } from '../i18n';

/**
 * Métadonnées d'affichage des fournisseurs LLM (T5 lot A, XPHOTO-26) — PUR & typé.
 *
 * Ces tables sont la source unique des libellés / défauts / indications propres à
 * chaque fournisseur pour l'écran « Configuration LLM ». Les DÉFAUTS (URL de base,
 * nom de variable d'env) doublent volontairement ceux du backend
 * (`ai/ai-service.ts`) — le front les propose à la saisie, le back reste la source
 * de vérité au test/à la découverte. Aucun secret ici : uniquement des NOMS de
 * variables d'env.
 */

/** Libellé i18n d'un fournisseur (dans le `<select>` Fournisseur). */
export const PROVIDER_LABEL: Record<AiProviderKind, MessageKey> = {
  openai_compat: 'llm.providerLmstudio',
  ollama: 'llm.providerOllama',
  anthropic: 'llm.providerClaude',
  gemini: 'llm.providerGemini',
  grok: 'llm.providerGrok',
};

/** URL de base par défaut proposée à la saisie (miroir du backend). */
export const DEFAULT_BASE_URL: Record<AiProviderKind, string> = {
  openai_compat: 'http://localhost:1234/v1',
  ollama: 'http://localhost:11434',
  anthropic: 'https://api.anthropic.com',
  gemini: 'https://generativelanguage.googleapis.com',
  grok: 'https://api.x.ai/v1',
};

/**
 * Nom de variable d'env conventionnel (vide = aucune clé requise).
 *
 * ⚠ Le serveur impose l'ALLOW-LIST `AI_API_KEY_ENV_PREFIX` (`XPHOTO_AI_KEY_…`) : un
 * nom hors préfixe est refusé (400) et ne serait de toute façon jamais résolu. Ces
 * défauts respectent donc le préfixe — la validation reste côté SERVEUR (never trust
 * the client), celle-ci n'est qu'une commodité de saisie.
 */
export const DEFAULT_API_KEY_ENV: Record<AiProviderKind, string> = {
  openai_compat: '',
  ollama: '',
  anthropic: `${AI_API_KEY_ENV_PREFIX}ANTHROPIC`,
  gemini: `${AI_API_KEY_ENV_PREFIX}GEMINI`,
  grok: `${AI_API_KEY_ENV_PREFIX}XAI`,
};

/** Lien console/documentation par fournisseur (indications). `null` = local (aucun). */
export const PROVIDER_DOC_URL: Record<AiProviderKind, string | null> = {
  openai_compat: 'https://lmstudio.ai/docs/local-server',
  ollama: 'https://ollama.com/library',
  anthropic: 'https://console.anthropic.com/',
  gemini: 'https://aistudio.google.com/apikey',
  grok: 'https://console.x.ai/',
};

/** Clé i18n de la NOTE d'aide propre à chaque fournisseur (panneau bas). */
export const PROVIDER_NOTE_KEY: Record<AiProviderKind, MessageKey> = {
  openai_compat: 'llm.noteLmstudio',
  ollama: 'llm.noteOllama',
  anthropic: 'llm.noteClaude',
  gemini: 'llm.noteGemini',
  grok: 'llm.noteGrok',
};

/** Un fournisseur exige-t-il une clé (cloud) ? LM Studio / Ollama sont locaux. */
export const REQUIRES_API_KEY: Record<AiProviderKind, boolean> = {
  openai_compat: false,
  ollama: false,
  anthropic: true,
  gemini: true,
  grok: true,
};

/** Ordre d'affichage des fournisseurs dans le `<select>` (LM Studio en tête). */
export const PROVIDER_ORDER: readonly AiProviderKind[] = [
  'openai_compat',
  'anthropic',
  'gemini',
  'grok',
  'ollama',
];

/** Libellé i18n d'un mode d'action IA. */
export const ACTION_MODE_LABEL: Record<AiActionMode, MessageKey> = {
  auto: 'llm.actionModeAuto',
  native_tools: 'llm.actionModeNative',
  json_in_text: 'llm.actionModeJson',
};

/** Ordre d'affichage des modes d'action. */
export const ACTION_MODE_ORDER: readonly AiActionMode[] = ['auto', 'native_tools', 'json_in_text'];
